import { useState } from 'react';
import { ptBR } from '@/i18n/pt-BR';
import { senhaForte } from '@/lib/senha_forte';
import type { ClienteAdmin } from '@/lib/plataforma_admin';
import { Blueprint, Button, Input, Select } from '@/components/ui';

interface NovoEscritorioFormProps {
  clientes: ClienteAdmin[];
  salvando: boolean;
  /** Resolve `true` só quando o servidor confirmou a criação. */
  onCriar: (payload: {
    nome: string;
    cnpj?: string | null;
    gestor_email: string;
    gestor_senha_provisoria: string;
    gestor_nome?: string | null;
    cliente_id?: string | null;
  }) => Promise<boolean>;
}

const _SO_NUMEROS = /^\d{14}$/;

/**
 * Criação de escritório pelo console, com o primeiro gestor no mesmo passo // ux-neutrality:allow doc-comment
 * (E65.F5). // ux-neutrality:allow doc-comment
 *
 * As duas decisões de contrato aparecem NA TELA, não só no payload: o // ux-neutrality:allow doc-comment
 * escritório nasce com a conta do gestor (senha provisória, troca obrigatória // ux-neutrality:allow doc-comment
 * no primeiro acesso) e o vínculo com o cliente é opcional — dá para criar já // ux-neutrality:allow doc-comment
 * vinculado, que é o que o dono da plataforma tentou fazer, ou vincular depois // ux-neutrality:allow doc-comment
 * pela ação que a tabela já tem. // ux-neutrality:allow doc-comment
 */
export function NovoEscritorioForm({ clientes, salvando, onCriar }: NovoEscritorioFormProps) {
  const [nome, setNome] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [gestorEmail, setGestorEmail] = useState('');
  const [gestorNome, setGestorNome] = useState('');
  const [senha, setSenha] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [validacao, setValidacao] = useState<string | null>(null);

  async function handleSubmit(evento: React.FormEvent) {
    evento.preventDefault();
    const nomeLimpo = nome.trim();
    if (nomeLimpo === '') {
      setValidacao(ptBR.admin.validationNomeEscritorioObrigatorio);
      return;
    }
    const cnpjLimpo = cnpj.trim();
    if (cnpjLimpo !== '' && !_SO_NUMEROS.test(cnpjLimpo)) {
      setValidacao(ptBR.admin.validationDocumento);
      return;
    }
    if (gestorEmail.trim() === '') {
      setValidacao(ptBR.admin.validationEmailObrigatorio);
      return;
    }
    if (!senhaForte(senha)) {
      setValidacao(ptBR.admin.validationSenhaCurta);
      return;
    }
    setValidacao(null);
    const criado = await onCriar({
      nome: nomeLimpo,
      cnpj: cnpjLimpo === '' ? null : cnpjLimpo,
      gestor_email: gestorEmail.trim(),
      gestor_senha_provisoria: senha,
      gestor_nome: gestorNome.trim() === '' ? null : gestorNome.trim(),
      cliente_id: clienteId === '' ? null : clienteId,
    });
    // Recusa preserva o que foi digitado (padrão da criação de pessoa): limpar
    // o formulário depois de um e-mail duplicado passaria a impressão de que o
    // escritório foi criado.
    if (!criado) return;
    setNome('');
    setCnpj('');
    setGestorEmail('');
    setGestorNome('');
    setSenha('');
    setClienteId('');
  }

  return (
    <Blueprint>
      <form
        onSubmit={(evento) => void handleSubmit(evento)}
        className="flex flex-col gap-sc-2 p-sc-3"
        data-testid="novo-escritorio-form"
      >
        <h3 className="text-body-md font-medium text-fg">{ptBR.admin.novoEscritorioTitle}</h3>
        <p className="text-body-sm text-fg-muted">{ptBR.admin.novoEscritorioHint}</p>

        <div className="flex flex-wrap gap-2">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioNome}</span>
            <Input
              value={nome}
              onChange={(evento) => setNome(evento.target.value)}
              data-testid="novo-escritorio-nome"
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioDocumento}</span>
            <Input
              value={cnpj}
              onChange={(evento) => setCnpj(evento.target.value)}
              inputMode="numeric"
              data-testid="novo-escritorio-cnpj"
            />
          </label>
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioCliente}</span>
            <Select
              value={clienteId}
              onChange={(evento) => setClienteId(evento.target.value)}
              data-testid="novo-escritorio-cliente"
            >
              <option value="">{ptBR.admin.novoEscritorioSemCliente}</option>
              {clientes.map((cliente) => (
                <option key={cliente.id} value={cliente.id}>
                  {cliente.nome}
                </option>
              ))}
            </Select>
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioGestorEmail}</span>
            <Input
              value={gestorEmail}
              onChange={(evento) => setGestorEmail(evento.target.value)}
              data-testid="novo-escritorio-gestor-email"
            />
          </label>
          <label className="flex min-w-[180px] flex-1 flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioGestorNome}</span>
            <Input
              value={gestorNome}
              onChange={(evento) => setGestorNome(evento.target.value)}
              data-testid="novo-escritorio-gestor-nome"
            />
          </label>
          <label className="flex min-w-[180px] flex-col gap-1 text-body-sm text-fg-muted">
            <span>{ptBR.admin.novoEscritorioGestorSenha}</span>
            <Input
              type="password"
              value={senha}
              onChange={(evento) => setSenha(evento.target.value)}
              aria-describedby="novo-escritorio-gestor-senha-regra"
              data-testid="novo-escritorio-gestor-senha"
            />
          </label>
        </div>
        <p id="novo-escritorio-gestor-senha-regra" className="text-caption text-fg-dim">
          {ptBR.senhaForte.regra}
        </p>

        {validacao !== null ? (
          <p
            role="alert"
            data-testid="novo-escritorio-validacao"
            className="text-body-sm text-danger-700"
          >
            {validacao}
          </p>
        ) : null}

        <div>
          <Button
            variant="secondary"
            type="submit"
            disabled={salvando}
            data-testid="novo-escritorio-submit"
          >
            {salvando ? ptBR.admin.novoEscritorioEnviando : ptBR.admin.novoEscritorioSubmit}
          </Button>
        </div>
      </form>
    </Blueprint>
  );
}
